
import React, { useEffect, useRef } from 'react';

interface FeatureVideoProps {
  videoUrl?: string;
  imageUrl?: string;
  altText?: string;
  arcadeEmbed?: string;
  section?: string; // Added section prop to identify which section is using this component
}

const FeatureVideo = ({ 
  videoUrl, 
  imageUrl, 
  altText = "Feature demonstration",
  arcadeEmbed,
  section
}: FeatureVideoProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Add event listener for Arcade iframe messages
    const onArcadeIframeMessage = (e: MessageEvent) => {
      if (e.origin !== 'https://demo.arcade.software' || !e.isTrusted) return;
      
      // Type assertion for e.data
      const data = e.data as { event: string; id: string };
      
      const arcadeIframe = document.querySelector(`iframe[src*=${data.id}]`) as HTMLIFrameElement | null;
      if (!arcadeIframe || !arcadeIframe.contentWindow) return;
      
      if (data.event === 'arcade-init') {
        arcadeIframe.contentWindow.postMessage({event: 'register-popout-handler'}, '*');
      }
      
      if (data.event === 'arcade-popout-open') {
        arcadeIframe.style.position = 'fixed';
        arcadeIframe.style.zIndex = '9999999';
      }
      
      if (data.event === 'arcade-popout-close') {
        arcadeIframe.style.position = 'absolute';
        arcadeIframe.style.zIndex = 'auto';
      }
    };
    
    window.addEventListener('message', onArcadeIframeMessage);
    
    return () => {
      window.removeEventListener('message', onArcadeIframeMessage);
    };
  }, []);
  
  // Handle error and retry mechanism for iframe loading
  const handleArcadeIframeError = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    console.error("Error loading Arcade embed iframe:", e);
    const iframe = e.currentTarget;
    iframe.style.display = 'none';
    
    // Try to reload the iframe after a short delay
    setTimeout(() => {
      iframe.style.display = 'block';
      const src = iframe.src;
      iframe.src = "";
      setTimeout(() => {
        iframe.src = src;
      }, 100);
    }, 1000);
  };
  
  // Determine which embed to use based on section
  const getDefaultEmbed = () => {
    if (section === "ai-assistants") {
      return '<!--ARCADE EMBED START--><div style="position: relative; padding-bottom: calc(56.27277459656429% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/NEA9zy7r69fJORliF46k?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Effortless Chatbot Customization and Integration" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" onError="this.onerror=null;setTimeout(() => {this.src=this.src;}, 1000)"></iframe></div><!--ARCADE EMBED END-->';
    } else if (section === "intelliforms") {
      return '<!--ARCADE EMBED START--><div style="position: relative; padding-bottom: calc(47.447916666666664% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/eRVuemrjVAkoOIObsj2s?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Crafting Intelligent Forms with AskSpot" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" onError="this.onerror=null;setTimeout(() => {this.src=this.src;}, 1000)"></iframe></div><!--ARCADE EMBED END-->';
    } else if (section === "flow-builder") {
      return '<!--ARCADE EMBED START--><div style="position: relative; padding-bottom: calc(56.22254758418741% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/N2chnWnl9gnZAGai5JRs?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Flow Builder Demo" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" onError="this.onerror=null;setTimeout(() => {this.src=this.src;}, 1000)"></iframe></div><!--ARCADE EMBED END-->';
    }
    
    // Default AI assistants embed as fallback
    return '<!--ARCADE EMBED START--><div style="position: relative; padding-bottom: calc(56.27277459656429% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/NEA9zy7r69fJORliF46k?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Effortless Chatbot Customization and Integration" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" onError="this.onerror=null;setTimeout(() => {this.src=this.src;}, 1000)"></iframe></div><!--ARCADE EMBED END-->';
  };
  
  return (
    <div className="w-full max-w-4xl mx-auto mb-16">
      <div className="relative rounded-xl overflow-hidden aspect-video shadow-lg" ref={containerRef}>
        {arcadeEmbed ? (
          <div 
            className="w-full h-full" 
            dangerouslySetInnerHTML={{ 
              __html: arcadeEmbed
            }}
          />
        ) : videoUrl ? (
          <video 
            className="w-full h-full object-cover"
            autoPlay 
            loop 
            muted 
            playsInline
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="w-full h-full">
            <div 
              className="w-full h-full" 
              dangerouslySetInnerHTML={{ 
                __html: getDefaultEmbed()
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureVideo;
